import {
  Api,
  CommentsIn,
  GetAllPublicationsApiV1PublicationsGetData,
  GetCommentsForPublicationApiV1PublicationsPubIdCommentsGetData,
  Img2ImgRequest,
  PublicationIn,
  SignInRequest,
  Txt2ImgRequest,
} from './Api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LOCAL_STORAGE_KEY_USER } from '@/constants/localStorage'
import { message } from 'antd'
import { UseQueryOptions } from '@tanstack/react-query'
import { User } from '@/feature/user/store'

export const apiClient = new Api({
  baseUrl: process.env.NEXT_PUBLIC_API_URL,
  customFetch: (input, init) => {
    const headers: Record<string, string> = {}
    let token = ''

    try {
      const userStore: User = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY_USER) || '',
      ).state
      token = userStore.token
    } catch (e) {}

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return fetch(input, {
      ...init,
      headers: {
        ...init?.headers,
        ...headers,
      },
    }).then(
      (res) => {
        if (!res.ok) {
          message.error(res.text())
        }

        return res
      },
      (err) => {
        console.log(err)
        return Promise.reject(err)
      },
    )
  },
})

export const useSdModels = () =>
  useQuery(
    [apiClient.api.getSdModelsApiV1CreationsSdModlesGet.name],
    apiClient.api.getSdModelsApiV1CreationsSdModlesGet.bind(apiClient, {
      format: 'json',
    }),
  )

export const useLoras = () =>
  useQuery(
    [apiClient.api.getLorasApiV1CreationsLorasGet.name],
    apiClient.api.getLorasApiV1CreationsLorasGet.bind(apiClient, {
      format: 'json',
    }),
  )

export const usePromptStyles = () =>
  useQuery(
    [apiClient.api.getStylesApiV1CreationsPromptStylesGet.name],
    apiClient.api.getStylesApiV1CreationsPromptStylesGet.bind(apiClient, {
      format: 'json',
    }),
  )

export const useSignUp = () =>
  useMutation(apiClient.api.signUpApiV1AuthSignupPost)

export const useSignIn = () =>
  useMutation(
    (data: SignInRequest) =>
      apiClient.api.loginWithAccessTokenApiV1AuthSigninPost(data, {
        format: 'json',
      }),
    {
      meta: {
        redirectToSignIn: false,
      },
    },
  )

export const useTxtToImg = () =>
  useMutation((data: Txt2ImgRequest) =>
    apiClient.api.txt2ImageApiV1CreationsTxt2ImgPost(data, {
      format: 'json',
    }),
  )

export const useImgToImg = () =>
  useMutation((data: Img2ImgRequest) =>
    apiClient.api.txt2ImageApiV1CreationsImg2ImgPost(data, {
      format: 'json',
    }),
  )

export const useUserInfo = (enabled: boolean) =>
  useQuery(
    [apiClient.api.getCurrentUserDetailsApiV1AuthUsersMeGet.name],
    apiClient.api.getCurrentUserDetailsApiV1AuthUsersMeGet.bind(apiClient, {
      format: 'json',
    }),
    {
      enabled,
    },
  )

export const QUERY_KEY_CREATIONS_RESULTS =
  apiClient.api.allResultsApiV1CreationsResultsGet.name

export const useCreationsResults = () =>
  useQuery([QUERY_KEY_CREATIONS_RESULTS], {
    queryFn: () =>
      apiClient.api.allResultsApiV1CreationsResultsGet({
        format: 'json',
      }),
  })

export const QUERY_KEY_CREATIONS_REQUESTS =
  apiClient.api.allProgressApiV1CreationsRequestsGet.name

export const useCreationsRequests = (
  options: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>,
) =>
  useQuery(
    [QUERY_KEY_CREATIONS_REQUESTS],
    apiClient.api.allProgressApiV1CreationsRequestsGet.bind(apiClient, {
      format: 'json',
    }),
    options,
  )

export const useControlNetModels = (
  options: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>,
) =>
  useQuery(
    [apiClient.api.getControlNetModelsApiV1CreationsControlnetModelsGet.name],
    apiClient.api.getControlNetModelsApiV1CreationsControlnetModelsGet.bind(
      apiClient,
      {
        format: 'json',
      },
    ),
    options,
  )

export const useControlNetModules = (
  options: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>,
) =>
  useQuery(
    [apiClient.api.getControlNetModulesApiV1CreationsControlnetModulesGet.name],
    apiClient.api.getControlNetModulesApiV1CreationsControlnetModulesGet.bind(
      apiClient,
      {
        format: 'json',
      },
    ),
    options,
  )

export const QUERY_KEY_CREATIONS_CURRENT_REQUEST =
  apiClient.api.getCurrentRequestApiV1CreationsRequestsCurrentGet.name

export const useCreationsCurrentRequest = (
  options?: Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<string>(
    [QUERY_KEY_CREATIONS_CURRENT_REQUEST],
    apiClient.api.getCurrentRequestApiV1CreationsRequestsCurrentGet.bind(
      apiClient,
      {
        format: 'json',
      },
    ),
    options,
  )

export const QUERY_KEY_CREATIONS_REQUEST_BY_ID =
  apiClient.api.progressApiV1CreationsRequestsRequestIdGet.name

export const useCreationsRequestById = (
  requestId: string,
  options: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<{
    status: 'processing' | 'done' | 'error'
    error?: string
    result: {
      progress: number
      eta_relative: number
    }
  }>(
    [QUERY_KEY_CREATIONS_REQUEST_BY_ID, requestId],
    () =>
      apiClient.api.progressApiV1CreationsRequestsRequestIdGet(requestId, {
        format: 'json',
      }),
    options,
  )

export const useCreationsRemove = () =>
  useMutation((requestId: string) =>
    apiClient.api
      .interruptApiV1CreationsRequestsRequestIdPut(requestId, {
        format: 'json',
      })
      .then(() =>
        apiClient.api.removeApiV1CreationsRequestsRequestIdDelete(requestId, {
          format: 'json',
        }),
      ),
  )

export const usePublications = () =>
  useQuery<GetAllPublicationsApiV1PublicationsGetData>(
    [apiClient.api.getAllPublicationsApiV1PublicationsGet.name],
    apiClient.api.getAllPublicationsApiV1PublicationsGet.bind(apiClient, {
      format: 'json',
    }),
  )

export const useCreatePublication = () =>
  useMutation((data: PublicationIn) =>
    apiClient.api.createPublicationApiV1PublicationsPost(data, {
      format: 'json',
    }),
  )

export const useDeletePublication = () =>
  useMutation((pubId: string) =>
    apiClient.api.deletePublicationApiV1PublicationsPubIdDelete(pubId, {
      format: 'json',
    }),
  )

export const usePublicationComments = (pubId: string) =>
  useQuery<GetCommentsForPublicationApiV1PublicationsPubIdCommentsGetData>(
    [
      apiClient.api.getCommentsForPublicationApiV1PublicationsPubIdCommentsGet
        .name,
      pubId,
    ],
    () => {
      return apiClient.api.getCommentsForPublicationApiV1PublicationsPubIdCommentsGet(
        pubId,
        {
          format: 'json',
        },
      )
    },
  )

export const useCreatePublicationComment = () =>
  useMutation((params: { pubId: string; data: CommentsIn }) =>
    apiClient.api.createCommentOnPublicationApiV1PublicationsPubIdCommentsPost(
      params.pubId,
      params.data,
    ),
  )

export const useReplyPublicationComment = () =>
  useMutation(
    (params: { pubId: string; parentCmtId: string; data: CommentsIn }) =>
      apiClient.api.replyToCommentApiV1PublicationsPubIdCommentsParentCmtIdPost(
        params.pubId,
        params.parentCmtId,
        params.data,
      ),
  )

export const useSamplers = () =>
  useQuery(
    [apiClient.api.getSamplersApiV1CreationsSamplersGet.name],
    apiClient.api.getSamplersApiV1CreationsSamplersGet.bind(apiClient, {
      format: 'json',
    }),
  )
